import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import Menu from "../../components/navbar/index.tsx";
import Footer from "../../components/footer/index.tsx";
import Logo from "../../assets/logo/logo.png";

import Hero from "./secctions/hero/index.tsx";
import Services from "./secctions/services/index.tsx";
import Clients from "./secctions/clients/index.tsx";
import Projects from "./secctions/projects/index.tsx";
import FormContact from "./secctions/formContact/index.tsx";

function ScrollToHash() {
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) return;

    const id = location.hash.replace("#", "");
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [location.hash]);

  return null;
}

const Home = () => {
  const location = useLocation();

  const sectionIds = useMemo(
    () => ["inicio", "servicios", "clientes", "proyectos", "contacto"],
    []
  );

  const [activeSection, setActiveSection] = useState<string>(() => {
    const hash = location.hash?.replace("#", "");
    return hash && sectionIds.includes(hash) ? hash : "inicio";
  });

  useEffect(() => {
    const hash = location.hash?.replace("#", "");
    if (hash && sectionIds.includes(hash)) setActiveSection(hash);
  }, [location.hash, sectionIds]);

  useEffect(() => {
    const sections = sectionIds
      .map((id) => document.getElementById(id))
      .filter(Boolean) as HTMLElement[];

    if (!sections.length) return;

    const getHeaderOffset = () => {
      const v = getComputedStyle(document.documentElement)
        .getPropertyValue("--nb-header-h")
        .trim();
      const n = Number.parseFloat(v);
      return Number.isFinite(n) ? n : 64;
    };

    let header = getHeaderOffset();

    const isNearBottom = () => {
      const buffer = 120;
      const scrollBottom = window.scrollY + window.innerHeight;
      const docHeight = document.documentElement.scrollHeight;
      return docHeight - scrollBottom <= buffer;
    };

    const createObserver = () =>
      new IntersectionObserver(
        (entries) => {
          if (isNearBottom()) {
            setActiveSection("contacto");
            return;
          }

          const visible = entries.filter((e) => e.isIntersecting);
          if (!visible.length) return;

          const topMost = visible.sort(
            (a, b) => a.boundingClientRect.top - b.boundingClientRect.top
          )[0];

          const id = (topMost.target as HTMLElement).id;
          if (id) setActiveSection(id);
        },
        {
          root: null,
          threshold: 0.01,
          rootMargin: `-${header + 10}px 0px -55% 0px`,
        }
      );

    let observer = createObserver();
    sections.forEach((s) => observer.observe(s));

    const onResize = () => {
      const next = getHeaderOffset();
      if (next === header) return;

      header = next;
      observer.disconnect();
      observer = createObserver();
      sections.forEach((s) => observer.observe(s));
    };

    const onScroll = () => {
      if (isNearBottom()) setActiveSection("contacto");
    };

    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll);
      observer.disconnect();
    };
  }, [sectionIds]);

  useEffect(() => {
    const newHash = `#${activeSection}`;
    if (window.location.hash !== newHash) {
      window.history.replaceState(null, "", newHash);
    }
  }, [activeSection]);

  return (
    <div className="app">
      <Menu activeSection={activeSection} />
      <ScrollToHash />

      {/* 🔥 HERO sin padding lateral */}
      <section id="inicio" className="section section--hero">
        <Hero />
      </section>

      <section id="servicios" className="section">
        <Services />
      </section>

      <section id="clientes" className="section">
        <Clients />
      </section>

      <section id="proyectos" className="section">
        <Projects />
      </section>

      <section id="contacto" className="section">
        <FormContact />
      </section>

      <Footer
        logoSrc={Logo}
        logoAlt="Arysoltec"
        slogan="Diseñamos espacios, conectamos personas."
        companyName="Integradora de soluciones para construcción y tecnología"
      />
    </div>
  );
};

export default Home;